import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useSubscription } from '@apollo/client';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const START_PLEVEL_RELEASE_SCRUB = gql`
  mutation StartPLevelReleaseScrub($rows: [PLevelReleaseScrubRowInput]) {
    startPLevelReleaseScrub(rows: $rows) {
      runId
      message
    }
  }
`;

const SUBSCRIBE_PLEVEL_RELEASE_SCRUB = gql`
  subscription {
    subscriptionPLevelReleaseScrubProgress {
      runId
      rowId
      releaseDate
      program
      booking
      participant
      name
      dob
      fin
      mrun
      champid
      odrAssessmentDate
      bookingTable
      bookingFromRelease
      bookingArray
      bookingEvents
      releaseDates
      warnings
    }
  }
`;

const cleanCell = (value: string) => {
  if (!value) return '';
  return value.replace(/\u00a0/g, ' ').replace(/\s*\n\s*/g, ' ').trim();
};

const normalizeHeader = (value: string) => {
  if (!value) return '';
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const detectDelimiter = (line: string) => {
  const tabCount = (line.match(/\t/g) || []).length;
  const commaCount = (line.match(/,/g) || []).length;
  if (tabCount > commaCount) return '\t';
  if (commaCount > 0) return ',';
  return '\t';
};

const splitDelimitedLine = (line: string, delimiter: string) => {
  if (delimiter === '\t') {
    return line.split('\t').map((value) => cleanCell(value));
  }

  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(cleanCell(current));
      current = '';
      continue;
    }

    current += char;
  }

  values.push(cleanCell(current));
  return values;
};

const parseReleaseDate = (value: string) => {
  const raw = cleanCell(value);
  if (!raw) return { raw, normalized: '' };

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return { raw, normalized: `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}` };
  }

  const match = raw.match(/(\d{1,2})[\/-](\d{1,2})(?:[\/-](\d{2,4}))?/);
  if (!match) return { raw, normalized: '' };

  let month = match[1];
  let day = match[2];
  let year = match[3] || '';

  if (!year) {
    year = '2025';
  }
  if (year.length === 2) {
    year = `20${year}`;
  }
  if (year.length === 4) {
    const first = Number(match[1]);
    const second = Number(match[2]);
    if (first > 12 && second <= 12) {
      day = match[1];
      month = match[2];
    }
    if (first <= 12 && second > 12) {
      month = match[1];
      day = match[2];
    }
  }

  if (!year || !month || !day) return { raw, normalized: '' };
  return { raw, normalized: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}` };
};

const normalizeChsDate = (value: string) => {
  const raw = cleanCell(value);
  if (!raw) return '';

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const match = raw.match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (!match) return '';
  let year = match[3];
  if (year.length === 2) year = `20${year}`;
  const month = match[1].padStart(2, '0');
  const day = match[2].padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toUtcDate = (value: string) => {
  if (!value) return null;
  const [yearRaw, monthRaw, dayRaw] = value.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

const normalizeBookingKey = (value: string) => `${value || ''}`.replace(/\D/g, '');

const normalizeName = (value: string) => {
  const raw = cleanCell(value).toUpperCase();
  if (!raw) return '';
  const noParens = raw.split('(')[0].trim();
  const noHyphens = noParens.replace(/-/g, ' ');
  return noHyphens.replace(/\s+/g, ' ').trim();
};

const buildNameKeys = (first: string, last: string) => {
  if (!first || !last) return [];
  const primary = `${last}|${first}`;
  const secondary = `${first}|${last}`;
  if (primary === secondary) return [primary];
  return [primary, secondary];
};

const getNameKeys = (value: string) => {
  const normalized = normalizeName(value);
  if (!normalized) return [];
  if (normalized.includes(',')) {
    const parts = normalized.split(',');
    const lastPart = (parts[0] || '').trim().split(' ')[0];
    const firstPart = (parts[1] || '').trim().split(' ')[0];
    return buildNameKeys(firstPart, lastPart);
  }
  const tokens = normalized.split(' ').filter(Boolean);
  if (tokens.length < 2) return [];
  const firstPart = tokens[0];
  const lastPart = tokens[tokens.length - 1];
  return buildNameKeys(firstPart, lastPart);
};

const formatShortDate = (value: string) => {
  if (!value) return '';
  const parsed = parseReleaseDate(value);
  if (!parsed.normalized) return value;
  const [year, month, day] = parsed.normalized.split('-');
  if (!year || !month || !day) return parsed.normalized;
  return `${month}/${day}/${year}`;
};

const formatReportDate = (value: string) => {
  if (!value) return '';
  const normalized = normalizeChsDate(value) || value;
  return formatShortDate(normalized);
};

const normalizeProgram = (value: string) => {
  const raw = cleanCell(value).toUpperCase();
  if (!raw) return '';
  if (raw === '-' || raw === 'N/A') return '';

  if (raw.startsWith('MIST')) return 'MIST';
  if (raw.startsWith('FIST')) return 'FIST';
  if (raw.startsWith('ODR')) return 'ODR';
  if (raw.startsWith('PRG')) return 'PRG_WMN';
  if (raw.startsWith('MATERNAL')) return 'PRG_WMN';

  return '';
};

type HeaderInfo = {
  hasHeader: boolean;
  indexMap: Record<string, number>;
};

const getHeaderInfo = (fields: string[]) => {
  const indexMap: Record<string, number> = {};

  fields.forEach((field, index) => {
    const key = normalizeHeader(field);
    if (!key) return;
    if (key === 'releasedate' || key === 'release' || key === 'released') indexMap.releasedate = index;
    if (key === 'program' || key === 'prog') indexMap.program = index;
    if (key === 'booking' || key === 'bookingnumber' || key === 'booking#') indexMap.booking = index;
    if (key === 'name') indexMap.name = index;
    if (key === 'dob' || key === 'dateofbirth') indexMap.dob = index;
    if (key === 'cid' || key === 'clientid') indexMap.cid = index;
  });

  return { hasHeader: Object.keys(indexMap).length > 0, indexMap } as HeaderInfo;
};

const getExpectedFieldCount = (lines: string[], delimiter: string, headerFields: string[]) => {
  const headerCount = headerFields.length;
  if (lines.length === 0) return headerCount;

  let maxCount = headerCount;
  const sampleLines = lines.slice(0, 5);

  for (const line of sampleLines) {
    const count = splitDelimitedLine(line, delimiter).length;
    if (count > maxCount) {
      maxCount = count;
    }
  }

  return maxCount;
};

const mergeWrappedLines = (lines: string[], delimiter: string, expectedFieldCount: number) => {
  if (lines.length === 0) return [];

  const merged: string[] = [];
  let buffer = '';

  for (const line of lines) {
    const trimmed = line.trimEnd();
    if (!trimmed) continue;

    let combined = trimmed;
    if (buffer) {
      combined = `${buffer} ${trimmed}`;
    }

    buffer = combined;

    const count = splitDelimitedLine(buffer, delimiter).length;
    if (count < expectedFieldCount) continue;

    merged.push(buffer);
    buffer = '';
  }

  if (buffer) {
    merged.push(buffer);
  }

  return merged;
};

const getFallbackMap = (fieldCount: number) => {
  let fallback: Record<string, number> = { releasedate: 0 };
  if (fieldCount >= 6) {
    fallback = {
      releasedate: 0,
      program: 1,
      booking: 2,
      name: 3,
      dob: 4,
      cid: 5,
    };
  }
  if (fieldCount === 5) {
    fallback = {
      releasedate: 0,
      booking: 1,
      name: 2,
      dob: 3,
      cid: 4,
    };
  }
  if (fieldCount === 4) {
    fallback = {
      releasedate: 0,
      program: 1,
      booking: 2,
      name: 3,
    };
  }
  return fallback;
};

const getFieldValue = (
  fields: string[],
  headerInfo: HeaderInfo,
  fallback: Record<string, number>,
  key: string,
) => {
  if (headerInfo.hasHeader) {
    const headerIndex = headerInfo.indexMap[key];
    if (headerIndex !== undefined) return fields[headerIndex] || '';
  }

  const fallbackIndex = fallback[key];
  if (fallbackIndex === undefined) return '';
  return fields[fallbackIndex] || '';
};

const getChsHeaderInfo = (fields: string[]) => {
  const indexMap: Record<string, number> = {};

  fields.forEach((field, index) => {
    const key = normalizeHeader(field);
    if (!key) return;
    if (key === 'bookingnumber' || key === 'booking_number' || key === 'booking') indexMap.booking = index;
    if (key === 'fin') indexMap.fin = index;
    if (key === 'mrn' || key === 'mrun') indexMap.mrun = index;
    if (key === 'name') indexMap.name = index;
    if (key === 'dob' || key === 'dateofbirth') indexMap.dob = index;
    if (key === 'race') indexMap.race = index;
    if (key === 'sex') indexMap.sex = index;
    if (key === 'admitdate' || key === 'admit_date') indexMap.admitDate = index;
    if (key === 'releasedate' || key === 'release_date') indexMap.releaseDate = index;
    if (key === 'pcode' || key === 'p_code') indexMap.pcode = index;
    if (key === 'pcodedate' || key === 'pcode_date' || key === 'p_code_date') indexMap.pcodeDate = index;
    if (key === 'pcodesource' || key === 'pcode_source' || key === 'p_code_source') indexMap.pcodeSource = index;
    if (key === 'building') indexMap.building = index;
  });

  return { hasHeader: Object.keys(indexMap).length > 0, indexMap } as ChsHeaderInfo;
};

const parseChsRows = (text: string) => {
  const cleaned = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = cleaned.split('\n').map((line) => line.trimEnd()).filter((line) => line.trim());

  if (lines.length === 0) {
    return { records: [], error: 'No CHS rows found.' };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headerFields = splitDelimitedLine(lines[0], delimiter);
  const headerInfo = getChsHeaderInfo(headerFields);
  if (!headerInfo.hasHeader) {
    return { records: [], error: 'CHS header not found.' };
  }

  const dataLines = lines.slice(1);
  const expectedFieldCount = getExpectedFieldCount(dataLines, delimiter, headerFields);
  const normalizedLines = mergeWrappedLines(dataLines, delimiter, expectedFieldCount);

  const records: ChsRecord[] = [];
  normalizedLines.forEach((line, index) => {
    const fields = splitDelimitedLine(line, delimiter);
    const bookingValue = fields[headerInfo.indexMap.booking] || '';
    const bookingKey = normalizeBookingKey(bookingValue);
    if (!bookingKey) return;

    records.push({
      booking: bookingKey,
      bookingKey,
      name: cleanCell(fields[headerInfo.indexMap.name] || ''),
      dob: normalizeChsDate(fields[headerInfo.indexMap.dob] || ''),
      race: cleanCell(fields[headerInfo.indexMap.race] || ''),
      sex: cleanCell(fields[headerInfo.indexMap.sex] || ''),
      fin: cleanCell(fields[headerInfo.indexMap.fin] || ''),
      mrun: cleanCell(fields[headerInfo.indexMap.mrun] || ''),
      admitDate: normalizeChsDate(fields[headerInfo.indexMap.admitDate] || ''),
      releaseDate: normalizeChsDate(fields[headerInfo.indexMap.releaseDate] || ''),
      pcode: cleanCell(fields[headerInfo.indexMap.pcode] || ''),
      pcodeDate: normalizeChsDate(fields[headerInfo.indexMap.pcodeDate] || ''),
      pcodeSource: cleanCell(fields[headerInfo.indexMap.pcodeSource] || ''),
      building: cleanCell(fields[headerInfo.indexMap.building] || ''),
      index,
    });
  });

  return { records, error: '' };
};

const buildChsIndex = (records: ChsRecord[]) => {
  const map: Record<string, ChsRecord[]> = {};
  for (const record of records) {
    const key = record.bookingKey;
    if (!key) continue;
    if (!map[key]) map[key] = [];
    map[key].push(record);
  }
  return map;
};

const buildChsNameIndex = (records: ChsRecord[]) => {
  const map: Record<string, ChsRecord[]> = {};
  for (const record of records) {
    const keys = getNameKeys(record.name);
    if (keys.length === 0) continue;
    for (const key of keys) {
      if (!map[key]) map[key] = [];
      map[key].push(record);
    }
  }
  return map;
};

const getPcodeRank = (value: string) => {
  if (!value) return 0;
  const char = value.trim()[1];
  const rank = Number.parseInt(char, 10);
  if (!Number.isFinite(rank)) return 0;
  return rank;
};

const selectPcodeRecord = (records: ChsRecord[], targetDate: string) => {
  if (!targetDate) return null;
  const target = toUtcDate(targetDate);
  if (!target) return null;

  const candidates = records
    .filter((record) => {
      if (!record.pcode) return false;
      if (!record.pcodeDate) return false;
      if (record.pcode.toUpperCase() === 'NO P LEVEL') return false;
      const recordDate = toUtcDate(record.pcodeDate);
      if (!recordDate) return false;
      const diffDays = Math.abs((recordDate.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays > 365) return false;
      return true;
    })
    .map((record) => {
      const recordDate = toUtcDate(record.pcodeDate) as Date;
      const diffDays = Math.abs((recordDate.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
      return {
        record,
        diffDays,
        rank: getPcodeRank(record.pcode),
        index: record.index,
      };
    });

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.diffDays !== b.diffDays) return a.diffDays - b.diffDays;
    if (a.rank !== b.rank) return b.rank - a.rank;
    return a.index - b.index;
  });

  return candidates[0].record;
};

const getRowTargetDate = (row: ParsedRow) => {
  const releaseParsed = parseReleaseDate(row.releaseDate || row.releaseDateRaw || '');
  if (releaseParsed.normalized) return releaseParsed.normalized;
  const normalizedRelease = normalizeChsDate(row.releaseDate || row.releaseDateRaw || '');
  if (normalizedRelease) return normalizedRelease;
  const normalizedAssessment = normalizeChsDate(row.odrAssessmentDate || '');
  if (normalizedAssessment) return normalizedAssessment;
  return '';
};

const selectChsRecordByProximity = (records: ChsRecord[], row: ParsedRow) => {
  if (records.length === 0) return null;
  const target = getRowTargetDate(row);
  if (!target) return records[0];
  const targetDate = toUtcDate(target);
  if (!targetDate) return records[0];

  let best = records[0];
  let bestDiff = Number.POSITIVE_INFINITY;

  for (const record of records) {
    const candidateValue = record.releaseDate || record.admitDate;
    if (!candidateValue) continue;
    const candidateDate = toUtcDate(candidateValue);
    if (!candidateDate) continue;
    const diff = Math.abs(candidateDate.getTime() - targetDate.getTime());
    if (diff < bestDiff) {
      best = record;
      bestDiff = diff;
      continue;
    }
    if (diff !== bestDiff) continue;
    const bestHasRelease = !!best.releaseDate;
    const currentHasRelease = !!record.releaseDate;
    if (currentHasRelease && !bestHasRelease) {
      best = record;
    }
  }

  if (bestDiff === Number.POSITIVE_INFINITY) return records[0];
  return best;
};

const collectChsMatchesForRow = (
  row: ParsedRow,
  chsIndex: Record<string, ChsRecord[]>,
  chsNameIndex: Record<string, ChsRecord[]>,
) => {
  const bookingKeys: string[] = [];
  const addKey = (value: string) => {
    const key = normalizeBookingKey(value);
    if (!key) return;
    if (bookingKeys.includes(key)) return;
    bookingKeys.push(key);
  };

  addKey(row.booking || row.bookingRaw || '');
  for (const booking of row.bookingArray || []) {
    addKey(booking);
  }

  const matches: ChsRecord[] = [];
  const seen = new Set<number>();
  for (const key of bookingKeys) {
    const list = chsIndex[key] || [];
    for (const record of list) {
      if (seen.has(record.index)) continue;
      seen.add(record.index);
      matches.push(record);
    }
  }

  if (matches.length === 0) {
    const nameKeys = getNameKeys(row.name);
    for (const key of nameKeys) {
      const list = chsNameIndex[key] || [];
      for (const record of list) {
        if (seen.has(record.index)) continue;
        seen.add(record.index);
        matches.push(record);
      }
    }
  }

  const primaryKey = normalizeBookingKey(row.booking || row.bookingRaw || '');
  const primaryRecords = primaryKey ? chsIndex[primaryKey] || [] : [];
  const primaryRecord = primaryRecords.length > 0 ? primaryRecords[0] : null;
  const chsRecord = primaryRecord || selectChsRecordByProximity(matches, row);

  return { bookingKeys, matches, chsRecord };
};

const applyChsToRow = (
  row: ParsedRow,
  chsIndex: Record<string, ChsRecord[]>,
  chsNameIndex: Record<string, ChsRecord[]>,
) => {
  const { matches, chsRecord } = collectChsMatchesForRow(row, chsIndex, chsNameIndex);

  let assessmentDate = row.odrAssessmentDate || '';
  if (!assessmentDate && matches.length > 0) {
    assessmentDate = matches[0].admitDate || '';
  }
  const assessmentDateFromChs = !row.odrAssessmentDate && !!assessmentDate;

  const releaseParsed = parseReleaseDate(row.releaseDate || row.releaseDateRaw || '');
  const releaseDate = releaseParsed.normalized || normalizeChsDate(row.releaseDate || row.releaseDateRaw || '');

  const assessmentTarget = normalizeChsDate(assessmentDate) || assessmentDate;

  const pcodeAssessment = selectPcodeRecord(matches, assessmentTarget);
  const pcodeRelease = selectPcodeRecord(matches, releaseDate);

  const warningKeys = new Set([
    'chs record not found',
    'NO PCODE near ODR Assessment Date',
    'NO PCODE near Release Date',
  ]);
  const previousWarnings = row.serverWarnings || [];
  const cleanedWarnings = previousWarnings.filter((warning) => {
    if (!warning) return false;
    const normalizedWarning = warning.toLowerCase();
    if (assessmentDateFromChs && normalizedWarning === 'odr assessment date not found') return false;
    if (warningKeys.has(warning)) return false;
    return true;
  });
  const mergedWarnings = new Set(cleanedWarnings);

  if (matches.length === 0) mergedWarnings.add('chs record not found');
  if (assessmentTarget && !pcodeAssessment) mergedWarnings.add('NO PCODE near ODR Assessment Date');
  if (releaseDate && !pcodeRelease) mergedWarnings.add('NO PCODE near Release Date');

  return {
    ...row,
    odrAssessmentDate: assessmentDate || row.odrAssessmentDate,
    chsBooking: chsRecord?.booking || row.chsBooking || '',
    chsName: chsRecord?.name || row.chsName || '',
    chsDob: chsRecord?.dob || row.chsDob || '',
    chsRace: chsRecord?.race || row.chsRace || '',
    chsSex: chsRecord?.sex || row.chsSex || '',
    chsFin: chsRecord?.fin || row.chsFin || '',
    chsMrun: chsRecord?.mrun || row.chsMrun || '',
    pcodeAtAssessment: pcodeAssessment?.pcode || row.pcodeAtAssessment || '',
    pcodeDateAtAssessment: pcodeAssessment?.pcodeDate || row.pcodeDateAtAssessment || '',
    pcodeSourceAtAssessment: pcodeAssessment?.pcodeSource || row.pcodeSourceAtAssessment || '',
    buildingAtAssessment: pcodeAssessment?.building || row.buildingAtAssessment || '',
    pcodeAtRelease: pcodeRelease?.pcode || row.pcodeAtRelease || '',
    pcodeDateAtRelease: pcodeRelease?.pcodeDate || row.pcodeDateAtRelease || '',
    pcodeSourceAtRelease: pcodeRelease?.pcodeSource || row.pcodeSourceAtRelease || '',
    buildingAtRelease: pcodeRelease?.building || row.buildingAtRelease || '',
    serverWarnings: Array.from(mergedWarnings),
  };
};

type ParsedRow = {
  index: number;
  releaseDateRaw: string;
  releaseDate: string;
  program: string;
  booking: string;
  bookingRaw: string;
  name: string;
  dob: string;
  cid: string;
  issues: string[];
  participant?: number;
  fin?: string;
  mrun?: string;
  champid?: string;
  sex?: string;
  race?: string;
  arrest_count?: number;
  odrAssessmentDate?: string;
  bookingTable?: string;
  bookingFromRelease?: string;
  bookingArray?: string[];
  bookingEvents?: string[];
  releaseDates?: string[];
  serverWarnings?: string[];
  chsBooking?: string;
  chsName?: string;
  chsDob?: string;
  chsRace?: string;
  chsSex?: string;
  chsFin?: string;
  chsMrun?: string;
  pcodeAtAssessment?: string;
  pcodeDateAtAssessment?: string;
  pcodeSourceAtAssessment?: string;
  buildingAtAssessment?: string;
  pcodeAtRelease?: string;
  pcodeDateAtRelease?: string;
  pcodeSourceAtRelease?: string;
  buildingAtRelease?: string;
};

type ChsRecord = {
  booking: string;
  bookingKey: string;
  name: string;
  dob: string;
  race: string;
  sex: string;
  fin: string;
  mrun: string;
  admitDate: string;
  releaseDate: string;
  pcode: string;
  pcodeDate: string;
  pcodeSource: string;
  building: string;
  index: number;
};

type ChsHeaderInfo = {
  hasHeader: boolean;
  indexMap: Record<string, number>;
};

const reportFields = [
  { key: 'name', label: 'Name' },
  { key: 'champid', label: 'Champ ID' },
  { key: 'dob', label: 'DOB' },
  { key: 'sex', label: 'Sex' },
  { key: 'race', label: 'Race' },
  { key: 'booking', label: 'Booking' },
  { key: 'fin', label: 'FIN' },
  { key: 'mrun', label: 'MRUN' },
  { key: 'program', label: 'ODR Program' },
  { key: 'arrest_count', label: 'Arrest Count' },
  { key: 'ODRAssessmentDate', label: 'ODR Assessment Date' },
  { key: 'pcode_date', label: 'P-Code Date (ODR Assessment)' },
  { key: 'pcode', label: 'P-Code (ODR Assessment)' },
  { key: 'pcode_source', label: 'P Code Source (ODR Assessment)' },
  { key: 'building', label: 'Jail Housing Location (ODR Assessment)' },
  { key: 'release_date', label: 'Release Date' },
  { key: 'pcode_date_r', label: 'P Code Date (Release)' },
  { key: 'pcode_r', label: 'P Code (Release)' },
  { key: 'pcode_source_r', label: 'P Code Source (Release)' },
  { key: 'building_r', label: 'Jail Housing Location (Release)' },
];

const escapeCsvValue = (value: string | number) => {
  const normalized = value ?? '';
  const asString = `${normalized}`;
  if (!asString) return '';
  if (!/[",\n]/.test(asString)) return asString;
  return `"${asString.replace(/"/g, '""')}"`;
};

const parseRows = (text: string) => {
  const cleaned = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = cleaned.split('\n').map((line) => line.trimEnd()).filter((line) => line.trim());

  if (lines.length === 0) {
    return { rows: [], error: 'No rows found.' };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headerFields = splitDelimitedLine(lines[0], delimiter);
  const headerInfo = getHeaderInfo(headerFields);
  const dataLines = headerInfo.hasHeader ? lines.slice(1) : lines;
  const expectedFieldCount = getExpectedFieldCount(dataLines, delimiter, headerFields);
  const normalizedLines = mergeWrappedLines(dataLines, delimiter, expectedFieldCount);

  const rows: ParsedRow[] = normalizedLines.map((line, index) => {
    const fields = splitDelimitedLine(line, delimiter);
    const fallback = getFallbackMap(fields.length);

    const releaseDateValue = getFieldValue(fields, headerInfo, fallback, 'releasedate');
    const programValue = getFieldValue(fields, headerInfo, fallback, 'program');
    const bookingValue = getFieldValue(fields, headerInfo, fallback, 'booking');
    const nameValue = getFieldValue(fields, headerInfo, fallback, 'name');
    const dobValue = getFieldValue(fields, headerInfo, fallback, 'dob');
    const cidValue = getFieldValue(fields, headerInfo, fallback, 'cid');

    const releaseInfo = parseReleaseDate(releaseDateValue);
    const normalizedProgram = normalizeProgram(programValue);

    const bookingRaw = cleanCell(bookingValue);
    const bookingDigits = bookingRaw.replace(/\D/g, '');

    const issues: string[] = [];
    if (!releaseInfo.normalized) issues.push('missing release date');
    if (!bookingDigits) issues.push('missing booking');
    if (!normalizedProgram) issues.push('missing program');
    if (!cleanCell(nameValue)) issues.push('missing name');

    return {
      index: index + 1,
      releaseDateRaw: releaseInfo.raw,
      releaseDate: releaseInfo.normalized,
      program: normalizedProgram,
      booking: bookingDigits,
      bookingRaw,
      name: cleanCell(nameValue),
      dob: cleanCell(dobValue),
      cid: cleanCell(cidValue),
      issues,
    };
  });

  return { rows, error: '' };
};

export const PLevelReleaseScrub = () => {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [sourceName, setSourceName] = useState('');
  const [error, setError] = useState('');
  const [chsSourceName, setChsSourceName] = useState('');
  const [chsError, setChsError] = useState('');
  const [chsIndex, setChsIndex] = useState<Record<string, ChsRecord[]>>({});
  const [chsNameIndex, setChsNameIndex] = useState<Record<string, ChsRecord[]>>({});
  const [runId, setRunId] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isProcessingChs, setIsProcessingChs] = useState(false);
  const [detailRowId, setDetailRowId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('import');
  const [resolvedByBooking, setResolvedByBooking] = useState<Record<string, { program?: string; participant?: number }>>({});
  const [resolvedByRowId, setResolvedByRowId] = useState<Record<number, { program?: string; participant?: number }>>({});
  const [startScrub] = useMutation(START_PLEVEL_RELEASE_SCRUB);

  const stats = useMemo(() => {
    const total = rows.length;
    const missingProgram = rows.filter((row) => {
      const bookingKey = `${row.booking || row.bookingRaw || ''}`.replace(/\D/g, '');
      const resolvedProgram = resolvedByRowId[row.index]?.program
        || (bookingKey ? resolvedByBooking[bookingKey]?.program : '');
      return !row.program && !resolvedProgram;
    }).length;
    const missingBooking = rows.filter((row) => !row.booking).length;
    const missingRelease = rows.filter((row) => !row.releaseDate).length;
    const withIssues = rows.filter((row) => row.issues.length > 0).length;

    return {
      total,
      missingProgram,
      missingBooking,
      missingRelease,
      withIssues,
    };
  }, [rows, resolvedByRowId, resolvedByBooking]);

  const hasChsData = Object.keys(chsIndex).length > 0;
  const canLoadChs = rows.length > 0 && !isRunning;
  const detailRow = detailRowId ? rows.find((row) => row.index === detailRowId) : null;
  const detailChsSummary = detailRow ? collectChsMatchesForRow(detailRow, chsIndex, chsNameIndex) : null;
  useEffect(() => {
    if (hasChsData) return;
    if (activeTab === 'import') return;
    if (activeTab === 'report') return;
    setActiveTab('import');
  }, [activeTab, hasChsData]);

  const getBookingKey = useCallback((row: ParsedRow) => {
    return `${row.booking || row.bookingRaw || ''}`.replace(/\D/g, '');
  }, []);

  const getResolvedProgram = useCallback((row: ParsedRow) => {
    const resolvedProgram = resolvedByRowId[row.index]?.program;
    if (resolvedProgram) return resolvedProgram;
    const bookingKey = getBookingKey(row);
    const bookingProgram = bookingKey ? resolvedByBooking[bookingKey]?.program : '';
    if (bookingProgram) return bookingProgram;
    return row.program;
  }, [getBookingKey, resolvedByBooking, resolvedByRowId]);

  const getResolvedParticipant = useCallback((row: ParsedRow) => {
    const resolvedParticipant = resolvedByRowId[row.index]?.participant;
    if (resolvedParticipant) return resolvedParticipant;
    const bookingKey = getBookingKey(row);
    if (!bookingKey) return row.participant;
    const bookingParticipant = resolvedByBooking[bookingKey]?.participant;
    if (bookingParticipant) return bookingParticipant;
    return row.participant;
  }, [getBookingKey, resolvedByBooking, resolvedByRowId]);

  const normalizePcodeValue = useCallback((value?: string) => {
    if (!value) return 'NO P LEVEL';
    const cleaned = `${value}`.toUpperCase().replace(/\s+/g, '');
    if (!cleaned) return 'NO P LEVEL';
    if (cleaned.includes('NO') && cleaned.includes('LEVEL')) return 'NO P LEVEL';
    if (/^P[0-4]$/.test(cleaned)) return cleaned;
    return 'NO P LEVEL';
  }, []);

  const getCombinedIssues = useCallback((row: ParsedRow) => {
    const displayProgram = getResolvedProgram(row);
    const displayIssues = row.issues.filter((issue) => {
      if (issue === 'missing program' && displayProgram) return false;
      return true;
    });
    const combined = new Set([...displayIssues, ...(row.serverWarnings || [])].filter(Boolean));
    if (hasChsData) {
      const assessmentValue = normalizePcodeValue(row.pcodeAtAssessment);
      if (assessmentValue === 'NO P LEVEL') combined.add('missing p level at assessment');
      const releaseValue = normalizePcodeValue(row.pcodeAtRelease);
      if (releaseValue === 'NO P LEVEL') combined.add('missing p level at release');
    }
    return Array.from(combined);
  }, [getResolvedProgram, hasChsData, normalizePcodeValue]);

  const unresolvedRows = useMemo(() => {
    return rows.filter((row) => getCombinedIssues(row).length > 0);
  }, [getCombinedIssues, rows]);

  const issueTotals = useMemo(() => {
    const totals: Record<string, number> = {
      'chs record not found': 0,
      'NO PCODE near ODR Assessment Date': 0,
      'NO PCODE near Release Date': 0,
      'missing p level at assessment': 0,
      'missing p level at release': 0,
    };
    rows.forEach((row) => {
      const issues = getCombinedIssues(row);
      issues.forEach((issue) => {
        if (totals[issue] === undefined) return;
        totals[issue] += 1;
      });
    });
    return totals;
  }, [getCombinedIssues, rows]);

  const reportRows = useMemo(() => {
    return rows.map((row) => {
      const mrunValue = row.chsMrun || row.mrun || '';
      return {
        name: row.chsName || row.name || '',
        champid: row.champid || row.cid || '',
        dob: formatReportDate(row.chsDob || row.dob || ''),
        sex: row.chsSex || row.sex || '',
        race: row.chsRace || row.race || '',
        booking: row.chsBooking || row.booking || row.bookingRaw || '',
        fin: row.chsFin || row.fin || '',
        mrun: mrunValue,
        program: getResolvedProgram(row) || '',
        arrest_count: row.arrest_count ?? '',
        ODRAssessmentDate: formatReportDate(row.odrAssessmentDate || ''),
        pcode_date: formatReportDate(row.pcodeDateAtAssessment || ''),
        pcode: row.pcodeAtAssessment || '',
        pcode_source: row.pcodeSourceAtAssessment || '',
        building: row.buildingAtAssessment || '',
        release_date: formatReportDate(row.releaseDate || row.releaseDateRaw || ''),
        pcode_date_r: formatReportDate(row.pcodeDateAtRelease || ''),
        pcode_r: row.pcodeAtRelease || '',
        pcode_source_r: row.pcodeSourceAtRelease || '',
        building_r: row.buildingAtRelease || '',
      };
    });
  }, [getResolvedProgram, rows]);

  const reportCsv = useMemo(() => {
    if (reportRows.length === 0) return '';
    const header = reportFields.map((field) => escapeCsvValue(field.label)).join(',');
    const lines = reportRows.map((row) =>
      reportFields.map((field) => escapeCsvValue((row as Record<string, string | number>)[field.key] ?? '')).join(',')
    );
    return [header, ...lines].join('\n');
  }, [reportRows]);

  const handleDownloadReport = useCallback(() => {
    if (!reportCsv) return;
    const blob = new Blob([reportCsv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plevel_report.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [reportCsv]);

  const pcodeStats = useMemo(() => {
    const base = {
      ODRi: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      ODRr: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      FISTi: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      FISTr: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      MISTi: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      MISTr: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      PRG_WMNi: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
      PRG_WMNr: { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'NO P LEVEL': 0 },
    } as Record<string, Record<string, number>>;

    if (!hasChsData) return base;

    rows.forEach((row) => {
      const program = getResolvedProgram(row);
      if (!program) return;
      const assessmentKey = `${program}i`;
      const releaseKey = `${program}r`;
      if (!base[assessmentKey]) return;
      if (!base[releaseKey]) return;
      const assessmentPcode = normalizePcodeValue(row.pcodeAtAssessment);
      const releasePcode = normalizePcodeValue(row.pcodeAtRelease);
      base[assessmentKey][assessmentPcode] += 1;
      base[releaseKey][releasePcode] += 1;
    });

    return base;
  }, [getResolvedProgram, hasChsData, normalizePcodeValue, rows]);

  const assessmentVsReleaseData = useMemo(() => {
    const systems = ['ODR', 'FIST', 'MIST', 'PRG_WMN'];
    return systems.map((system) => {
      const assessmentKey = `${system}i`;
      const releaseKey = `${system}r`;
      const assessmentTotals = pcodeStats[assessmentKey];
      const releaseTotals = pcodeStats[releaseKey];
      return {
        name: system,
        Assessment_Total: assessmentTotals.P0 + assessmentTotals.P1 + assessmentTotals.P2 + assessmentTotals.P3 + assessmentTotals.P4,
        Release_Total: releaseTotals.P0 + releaseTotals.P1 + releaseTotals.P2 + releaseTotals.P3 + releaseTotals.P4,
      };
    });
  }, [pcodeStats]);

  const assessmentVsReleaseTotals = useMemo(() => {
    return assessmentVsReleaseData.reduce(
      (acc, row) => ({
        Assessment_Total: acc.Assessment_Total + row.Assessment_Total,
        Release_Total: acc.Release_Total + row.Release_Total,
      }),
      { Assessment_Total: 0, Release_Total: 0 }
    );
  }, [assessmentVsReleaseData]);

  const priorityBySystemData = useMemo(() => {
    const systems = ['ODR', 'FIST', 'MIST', 'PRG_WMN'];
    const rowsData: Array<Record<string, any>> = [];
    systems.forEach((system) => {
      const assessmentKey = `${system}i`;
      const releaseKey = `${system}r`;
      rowsData.push({
        name: `${system} - Assessment`,
        P0: pcodeStats[assessmentKey].P0,
        P1: pcodeStats[assessmentKey].P1,
        P2: pcodeStats[assessmentKey].P2,
        P3: pcodeStats[assessmentKey].P3,
        P4: pcodeStats[assessmentKey].P4,
      });
      rowsData.push({
        name: `${system} - Release`,
        P0: pcodeStats[releaseKey].P0,
        P1: pcodeStats[releaseKey].P1,
        P2: pcodeStats[releaseKey].P2,
        P3: pcodeStats[releaseKey].P3,
        P4: pcodeStats[releaseKey].P4,
      });
    });
    return rowsData;
  }, [pcodeStats]);

  const priorityTotals = useMemo(() => {
    return priorityBySystemData.reduce(
      (acc, row) => ({
        P0: acc.P0 + row.P0,
        P1: acc.P1 + row.P1,
        P2: acc.P2 + row.P2,
        P3: acc.P3 + row.P3,
        P4: acc.P4 + row.P4,
      }),
      { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0 }
    );
  }, [priorityBySystemData]);

  const pcodeMissingCounts = useMemo(() => {
    if (!hasChsData) return { assessmentMissing: 0, releaseMissing: 0 };
    let assessmentMissing = 0;
    let releaseMissing = 0;
    rows.forEach((row) => {
      const assessmentValue = normalizePcodeValue(row.pcodeAtAssessment);
      const releaseValue = normalizePcodeValue(row.pcodeAtRelease);
      if (assessmentValue === 'NO P LEVEL') assessmentMissing += 1;
      if (releaseValue === 'NO P LEVEL') releaseMissing += 1;
    });
    return { assessmentMissing, releaseMissing };
  }, [hasChsData, normalizePcodeValue, rows]);

  const renderRowsTable = useCallback((rowsToRender: ParsedRow[], emptyMessage: string, title: string) => {
    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700">
          {title}
        </div>
        {rowsToRender.length === 0 && (
          <div className="p-6 text-sm text-gray-500">{emptyMessage}</div>
        )}
        {rowsToRender.length > 0 && (
          <div className="max-h-[60vh] overflow-auto">
            <table className="min-w-full text-xs leading-tight">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-2 py-0.5 text-left w-[42px]">#</th>
                  <th className="px-2 py-0.5 text-left w-[96px]">Release Date</th>
                  <th className="px-2 py-0.5 text-left w-[140px]">Program</th>
                  <th className="px-2 py-0.5 text-left w-[110px]">Booking</th>
                  <th className="px-2 py-0.5 text-left min-w-[160px]">Name</th>
                  <th className="px-2 py-0.5 text-left w-[140px]">CID / ChampID</th>
                  <th className="px-2 py-0.5 text-left w-[120px]">ODR Assessment</th>
                  <th className="px-2 py-0.5 text-left min-w-[160px]">Release Dates</th>
                  <th className="px-2 py-0.5 text-left min-w-[190px]">Booking Numbers</th>
                  <th className="px-2 py-0.5 text-left min-w-[160px]">Issues</th>
                  <th className="px-2 py-0.5 text-left w-[70px]">Details</th>
                </tr>
              </thead>
              <tbody>
                {rowsToRender.map((row) => {
                  const releaseDatesValue = row.releaseDates?.length ? row.releaseDates.join(', ') : '-';
                  const bookingArray = row.bookingArray || [];
                  const releaseDateDisplay = formatShortDate(row.releaseDate || row.releaseDateRaw);
                  const rowStripe = row.index % 2 === 0 ? 'bg-gray-50' : 'bg-white';
                  const idValue = row.cid || row.champid || '-';
                  const primaryBooking = row.booking || row.bookingRaw;
                  const bookingKey = getBookingKey(row);
                  const resolvedProgram = resolvedByRowId[row.index]?.program
                    || (bookingKey ? resolvedByBooking[bookingKey]?.program : '');
                  const resolvedParticipant = getResolvedParticipant(row);
                  const displayProgram = resolvedProgram || row.program;
                  const bookingNumbers: string[] = [];
                  if (primaryBooking) {
                    bookingNumbers.push(primaryBooking);
                  }
                  for (const booking of bookingArray) {
                    if (!booking) continue;
                    if (bookingNumbers.includes(booking)) continue;
                    bookingNumbers.push(booking);
                  }
                  const bookingPreview: string[] = [];
                  let previewLength = 0;
                  for (const booking of bookingNumbers) {
                    const token = bookingPreview.length === 0 ? booking : `, ${booking}`;
                    if (previewLength + token.length > 30) break;
                    bookingPreview.push(booking);
                    previewLength += token.length;
                  }
                  const isTruncated = bookingPreview.length < bookingNumbers.length;
                  const combinedIssues = getCombinedIssues(row);
                  return (
                    <React.Fragment key={`${row.index}-${row.bookingRaw}`}>
                      <tr className={rowStripe}>
                        <td className="px-2 py-0.5 text-gray-500">{row.index}</td>
                        <td className="px-2 py-0.5 text-gray-900 whitespace-nowrap">{releaseDateDisplay || '-'}</td>
                        <td className="px-2 py-0.5">
                          <div className="text-gray-900">
                            {displayProgram || '-'}
                            {row.bookingTable && ` · ${row.bookingTable}`}
                          </div>
                        </td>
                        <td className="px-2 py-0.5 text-gray-900">{row.booking || row.bookingRaw || '-'}</td>
                        <td className="px-2 py-0.5 text-gray-900">{row.name || '-'}</td>
                        <td className="px-2 py-0.5 text-gray-900">{idValue}</td>
                        <td className="px-2 py-0.5 text-gray-900 whitespace-nowrap">{row.odrAssessmentDate || '-'}</td>
                        <td className="px-2 py-0.5 text-gray-600">{releaseDatesValue}</td>
                        <td className="px-2 py-0.5 text-gray-600">
                          {bookingNumbers.length === 0 && '-'}
                          {bookingNumbers.length > 0 && (
                            <span>
                              {bookingPreview.map((booking, index) => {
                                const isPrimary = primaryBooking ? `${booking}` === `${primaryBooking}` : true;
                                const valueClass = isPrimary ? 'text-gray-900' : 'text-gray-400';
                                return (
                                  <span key={`${row.index}-${booking}`} className={valueClass}>
                                    {booking}{index < bookingPreview.length - 1 ? ', ' : ''}
                                  </span>
                                );
                              })}
                              {isTruncated && <span className="text-gray-400">…</span>}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-0.5 text-gray-600">
                          {combinedIssues.length === 0 && <span className="text-green-600">OK</span>}
                          {combinedIssues.length > 0 && (
                            <span className="text-red-600">{combinedIssues.join(', ')}</span>
                          )}
                        </td>
                        <td className="px-2 py-0.5 text-gray-600">
                          <button
                            type="button"
                            className="text-xs text-gray-600 hover:text-gray-900"
                            onClick={() => setDetailRowId(row.index)}
                          >
                            Show
                          </button>
                        </td>
                      </tr>
                      <Dialog open={detailRowId === row.index} onOpenChange={(open: boolean) => {
                        if (open) return;
                        setDetailRowId(null);
                      }}>
                      <DialogContent className="max-w-6xl">
                        <DialogHeader>
                          <DialogTitle>Row {row.index}</DialogTitle>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                            <span className="font-semibold text-gray-700">A.txt</span>
                            <span>Release: {row.releaseDateRaw || row.releaseDate || '-'}</span>
                            <span>Booking: {row.bookingRaw || row.booking || '-'}</span>
                            <span>Program: {row.program || '-'}</span>
                            <span>Name: {row.name || '-'}</span>
                            <span>DOB: {row.dob || '-'}</span>
                            <span>CID: {row.cid || '-'}</span>
                          </div>
                        </DialogHeader>
                        {detailRow && (
                          <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
                            <div className="space-y-4">
                              <div>
                                <div className="text-xs font-semibold text-gray-500 uppercase">Issues</div>
                                <div className="mt-1">
                                  {combinedIssues.length === 0 && <span className="text-green-600">OK</span>}
                                  {combinedIssues.length > 0 && (
                                    <span className="text-red-600">{combinedIssues.join(', ')}</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-500 uppercase">Resolution Path</div>
                                <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                  <div>
                                    <span className="text-gray-400">Program Source:</span>{' '}
                                    {resolvedByRowId[detailRow.index]?.program ? 'server resolver' : detailRow.program ? 'input' : 'missing'}
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Participant Source:</span>{' '}
                                    {resolvedParticipant || detailRow.participant ? 'server resolver' : 'missing'}
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Assessment Date Source:</span>{' '}
                                    {detailRow.odrAssessmentDate && detailChsSummary?.matches?.some((record) => record.admitDate && record.admitDate === detailRow.odrAssessmentDate)
                                      ? 'CHS admit_date'
                                      : detailRow.odrAssessmentDate
                                        ? 'server'
                                        : 'missing'}
                                  </div>
                                  <div>
                                    <span className="text-gray-400">CHS Matches:</span>{' '}
                                    {detailChsSummary?.matches?.length || 0}
                                  </div>
                                  <div className="sm:col-span-2">
                                    <span className="text-gray-400">CHS Booking Keys:</span>{' '}
                                    {detailChsSummary?.bookingKeys?.length ? detailChsSummary.bookingKeys.join(', ') : '-'}
                                  </div>
                                  <div className="sm:col-span-2">
                                    <span className="text-gray-400">CHS Booking Used:</span>{' '}
                                    {detailChsSummary?.chsRecord?.booking || '-'}
                                  </div>
                                </div>
                              </div>
                              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                <div className="text-xs font-semibold text-gray-500 uppercase">Scrubber Data</div>
                                <div className="mt-2">
                                  <div className="text-xs font-semibold text-gray-500 uppercase">Input</div>
                                  <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                    <div><span className="text-gray-400">Release Date:</span> {detailRow.releaseDateRaw || '-'}</div>
                                    <div><span className="text-gray-400">Program:</span> {detailRow.program || '-'}</div>
                                    <div><span className="text-gray-400">Booking:</span> {detailRow.bookingRaw || '-'}</div>
                                    <div><span className="text-gray-400">Name:</span> {detailRow.name || '-'}</div>
                                    <div><span className="text-gray-400">DOB:</span> {detailRow.dob || '-'}</div>
                                    <div><span className="text-gray-400">CID:</span> {detailRow.cid || '-'}</div>
                                  </div>
                                  <div className="mt-3 text-xs font-semibold text-gray-500 uppercase">Resolved</div>
                                  <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                    <div><span className="text-gray-400">Program:</span> {displayProgram || '-'}</div>
                                    <div><span className="text-gray-400">Participant:</span> {resolvedParticipant || detailRow.participant || '-'}</div>
                                    <div><span className="text-gray-400">Primary Booking:</span> {detailRow.booking || detailRow.bookingRaw || '-'}</div>
                                    <div><span className="text-gray-400">Booking Table:</span> {detailRow.bookingTable || '-'}</div>
                                    <div><span className="text-gray-400">Booking From Release:</span> {detailRow.bookingFromRelease || '-'}</div>
                                    <div><span className="text-gray-400">Booking Numbers:</span> {bookingNumbers.length === 0 ? '-' : bookingNumbers.join(', ')}</div>
                                    <div><span className="text-gray-400">Booking Events:</span> {detailRow.bookingEvents?.length ? detailRow.bookingEvents.join(', ') : '-'}</div>
                                    <div><span className="text-gray-400">Release Dates:</span> {releaseDatesValue}</div>
                                    <div><span className="text-gray-400">ODR Assessment Date:</span> {detailRow.odrAssessmentDate || '-'}</div>
                                    <div><span className="text-gray-400">FIN:</span> {detailRow.fin || '-'}</div>
                                    <div><span className="text-gray-400">MRUN:</span> {detailRow.mrun || '-'}</div>
                                    <div><span className="text-gray-400">ChampID:</span> {detailRow.champid || '-'}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                <div className="text-xs font-semibold text-gray-500 uppercase">CHS Data</div>
                                <div className="mt-2">
                                  <div className="text-xs font-semibold text-gray-500 uppercase">CHS Record</div>
                                  <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                    <div><span className="text-gray-400">CHS Booking:</span> {detailRow.chsBooking || '-'}</div>
                                    <div><span className="text-gray-400">CHS Name:</span> {detailRow.chsName || '-'}</div>
                                    <div><span className="text-gray-400">CHS DOB:</span> {detailRow.chsDob || '-'}</div>
                                    <div><span className="text-gray-400">CHS Race:</span> {detailRow.chsRace || '-'}</div>
                                    <div><span className="text-gray-400">CHS Sex:</span> {detailRow.chsSex || '-'}</div>
                                    <div><span className="text-gray-400">CHS FIN:</span> {detailRow.chsFin || '-'}</div>
                                    <div><span className="text-gray-400">CHS MRN:</span> {detailRow.chsMrun || '-'}</div>
                                    <div><span className="text-gray-400">CHS Admit:</span> {detailChsSummary?.chsRecord?.admitDate || '-'}</div>
                                    <div><span className="text-gray-400">CHS Release:</span> {detailChsSummary?.chsRecord?.releaseDate || '-'}</div>
                                  </div>
                                  <div className="mt-3 text-xs font-semibold text-gray-500 uppercase">PCode Results</div>
                                  <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                    <div><span className="text-gray-400">PCode @ Assessment:</span> {detailRow.pcodeAtAssessment || '-'}</div>
                                    <div><span className="text-gray-400">PCode Date @ Assessment:</span> {detailRow.pcodeDateAtAssessment || '-'}</div>
                                    <div><span className="text-gray-400">PCode Source @ Assessment:</span> {detailRow.pcodeSourceAtAssessment || '-'}</div>
                                    <div><span className="text-gray-400">Building @ Assessment:</span> {detailRow.buildingAtAssessment || '-'}</div>
                                    <div><span className="text-gray-400">PCode @ Release:</span> {detailRow.pcodeAtRelease || '-'}</div>
                                    <div><span className="text-gray-400">PCode Date @ Release:</span> {detailRow.pcodeDateAtRelease || '-'}</div>
                                    <div><span className="text-gray-400">PCode Source @ Release:</span> {detailRow.pcodeSourceAtRelease || '-'}</div>
                                    <div><span className="text-gray-400">Building @ Release:</span> {detailRow.buildingAtRelease || '-'}</div>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-500 uppercase">CHS Matches</div>
                                <div className="mt-1 text-xs text-gray-600">
                                  {detailChsSummary?.matches?.length === 0 && <div>No CHS matches found.</div>}
                                  {detailChsSummary?.matches?.length ? (
                                    <div className="grid gap-1">
                                      {detailChsSummary.matches.slice(0, 10).map((record) => (
                                        <div key={`${record.bookingKey}-${record.index}`} className="flex flex-wrap gap-x-3">
                                          <span><span className="text-gray-400">Booking:</span> {record.booking}</span>
                                          <span><span className="text-gray-400">PCode:</span> {record.pcode || '-'}</span>
                                          <span><span className="text-gray-400">PCode Date:</span> {record.pcodeDate || '-'}</span>
                                          <span><span className="text-gray-400">Admit:</span> {record.admitDate || '-'}</span>
                                          <span><span className="text-gray-400">Release:</span> {record.releaseDate || '-'}</span>
                                        </div>
                                      ))}
                                      {detailChsSummary.matches.length > 10 && (
                                        <div className="text-gray-500">Showing first 10 matches.</div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                      </Dialog>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }, [detailRow, detailRowId, detailChsSummary, getBookingKey, getCombinedIssues, getResolvedParticipant, resolvedByBooking, resolvedByRowId]);

  const startScrubForRows = useCallback(async (rowsToScrub: ParsedRow[]) => {
    if (!rowsToScrub.length) return;

    setIsRunning(true);
    const payloadRows = rowsToScrub.map((row) => ({
      rowId: row.index,
      releaseDate: row.releaseDate || row.releaseDateRaw,
      program: row.program,
      booking: row.booking || row.bookingRaw,
      name: row.name,
      dob: row.dob,
      cid: row.cid,
    }));

    const response = await startScrub({ variables: { rows: payloadRows } });
    const result = response?.data?.startPLevelReleaseScrub;
    if (!result?.runId) {
      setIsRunning(false);
      return;
    }

    setRunId(result.runId);
  }, [startScrub]);

  const handleReleaseFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const result = parseRows(text);
    setRows(result.rows);
    setSourceName(file.name);
    setError(result.error);
    setRunId('');
    setIsRunning(false);
    setResolvedByBooking({});
    setResolvedByRowId({});
    setChsSourceName('');
    setChsError('');
    setChsIndex({});
    setChsNameIndex({});

    await startScrubForRows(result.rows);
  }, [startScrubForRows]);

  const handleChsFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessingChs(true);
    try {
      const text = await file.text();
      const result = parseChsRows(text);
      setChsError(result.error);
      setChsSourceName(file.name);
      const index = buildChsIndex(result.records);
      const nameIndex = buildChsNameIndex(result.records);
      setChsIndex(index);
      setChsNameIndex(nameIndex);
      if (result.records.length === 0) return;
      setRows((prevRows) => prevRows.map((row) => applyChsToRow(row, index, nameIndex)));
    } finally {
      setIsProcessingChs(false);
    }
  }, []);

  const handleClear = useCallback(() => {
    setRows([]);
    setSourceName('');
    setError('');
    setRunId('');
    setIsRunning(false);
    setIsProcessingChs(false);
    setResolvedByBooking({});
    setResolvedByRowId({});
    setChsSourceName('');
    setChsError('');
    setChsIndex({});
    setChsNameIndex({});
  }, []);

  const applyScrubPayload = useCallback((payload: any) => {
    if (!payload) return;
    const sameRun = runId ? payload.runId === runId : true;
    if (!sameRun) return;

    if (payload.warnings?.includes('scrub_complete')) {
      setIsRunning(false);
      return;
    }

    if (payload.booking) {
      const bookingKey = `${payload.booking}`.replace(/\D/g, '');
      if (bookingKey) {
        setResolvedByBooking((prev) => ({
          ...prev,
          [bookingKey]: {
            program: payload.program || prev[bookingKey]?.program,
            participant: payload.participant || prev[bookingKey]?.participant,
          },
        }));
      }
    }
    const payloadRowId = Number(payload.rowId);
    if (Number.isFinite(payloadRowId) && payloadRowId > 0) {
      setResolvedByRowId((prev) => ({
        ...prev,
        [payloadRowId]: {
          program: payload.program || prev[payloadRowId]?.program,
          participant: payload.participant || prev[payloadRowId]?.participant,
        },
      }));
    }

    setRows((prevRows) =>
      prevRows.map((row) => {
        const payloadBooking = `${payload.booking || ''}`.replace(/\D/g, '');
        const rowBooking = `${row.booking || row.bookingRaw || ''}`.replace(/\D/g, '');
        const payloadRowId = Number(payload.rowId);
        const hasRowId = Number.isFinite(payloadRowId) && payloadRowId > 0;
        const matchesById = hasRowId && row.index === payloadRowId;
        const matchesByBooking = !hasRowId && payloadBooking && rowBooking && payloadBooking === rowBooking;
        if (!matchesById && !matchesByBooking) return row;
        const updatedProgram = payload.program || row.program;
        const updatedBooking = payload.booking || row.booking || row.bookingRaw;
        const updatedReleaseDate = payload.releaseDate || row.releaseDate || row.releaseDateRaw;
        const updatedName = payload.name || row.name;
        const updatedIssues = row.issues.filter((issue) => {
          if (issue === 'missing program' && updatedProgram) return false;
          if (issue === 'missing booking' && updatedBooking) return false;
          if (issue === 'missing release date' && updatedReleaseDate) return false;
          if (issue === 'missing name' && updatedName) return false;
          return true;
        });

        const updatedRow = {
          ...row,
          program: updatedProgram,
          participant: payload.participant || row.participant,
          dob: payload.dob || row.dob,
          fin: payload.fin || row.fin,
          mrun: payload.mrun || row.mrun,
          champid: payload.champid || row.champid,
          odrAssessmentDate: payload.odrAssessmentDate || row.odrAssessmentDate,
          bookingTable: payload.bookingTable || row.bookingTable,
          bookingFromRelease: payload.bookingFromRelease || row.bookingFromRelease,
          bookingArray: payload.bookingArray || row.bookingArray,
          bookingEvents: payload.bookingEvents || row.bookingEvents,
          releaseDates: payload.releaseDates || row.releaseDates,
          issues: updatedIssues,
          serverWarnings: payload.warnings || row.serverWarnings,
        };
        if (!hasChsData) return updatedRow;
        return applyChsToRow(updatedRow, chsIndex, chsNameIndex);
      })
    );
  }, [runId, chsIndex, chsNameIndex, hasChsData]);

  useSubscription(SUBSCRIBE_PLEVEL_RELEASE_SCRUB, {
    onData: ({ data }) => {
      const payload = data.data?.subscriptionPLevelReleaseScrubProgress;
      applyScrubPayload(payload);
    },
  });

  return (
    <div className="p-6 space-y-6">
      {(isRunning || isProcessingChs) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow">
            {isRunning && <div>Scrubbing release data...</div>}
            {isProcessingChs && <div>Processing CHS report...</div>}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">PLevel Report</h1>
        <p className="text-sm text-gray-600">
          Load the combined release list (releasedate, program, booking, name, dob, cid). Scrubbing starts
          automatically after upload.
        </p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="report" disabled={rows.length === 0}>Report</TabsTrigger>
          <TabsTrigger value="stats" disabled={!hasChsData}>Stats</TabsTrigger>
          <TabsTrigger value="graphics" disabled={!hasChsData}>Graphics</TabsTrigger>
          <TabsTrigger value="unresolved" disabled={!hasChsData}>Unresolved</TabsTrigger>
        </TabsList>
        <TabsContent value="import" className="space-y-6">
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                id="plevel-release-file"
                className="hidden"
                accept=".txt,.csv"
                onChange={handleReleaseFileChange}
              />
              <Button asChild>
                <label htmlFor="plevel-release-file" className={cn(isRunning && 'pointer-events-none opacity-60 cursor-not-allowed')}>
                  {isRunning ? 'Scrubbing...' : 'Choose Release File'}
                </label>
              </Button>
              <input
                type="file"
                id="plevel-chs-file"
                className="hidden"
                accept=".csv"
                onChange={handleChsFileChange}
              />
              <Button variant="outline" asChild>
                <label htmlFor="plevel-chs-file" className={cn(!canLoadChs && 'pointer-events-none opacity-60 cursor-not-allowed')}>
                  Load CHS Report
                </label>
              </Button>
              <Button variant="outline" onClick={handleClear}>
                Clear
              </Button>
              {sourceName && (
                <span className="text-sm text-gray-500">Source: {sourceName}</span>
              )}
              {chsSourceName && (
                <span className="text-sm text-gray-500">CHS: {chsSourceName}{hasChsData ? ' (loaded)' : ''}</span>
              )}
              {runId && (
                <span className="text-sm text-gray-500">Run: {runId}</span>
              )}
            </div>

            <div className="text-xs text-gray-500">
              Expected columns: releasedate (MM/DD or YYYY-MM-DD), program (optional), booking, name, dob (optional), cid
              (optional). Tabs or commas are accepted.
            </div>

            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}
            {chsError && (
              <div className="text-sm text-red-600">CHS: {chsError}</div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 text-sm">
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Rows</div>
                <div className="text-lg font-semibold text-gray-900">{stats.total}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Missing Program</div>
                <div className="text-lg font-semibold text-gray-900">{stats.missingProgram}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Missing Booking</div>
                <div className="text-lg font-semibold text-gray-900">{stats.missingBooking}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Missing Release</div>
                <div className="text-lg font-semibold text-gray-900">{stats.missingRelease}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Rows With Issues</div>
                <div className="text-lg font-semibold text-gray-900">{stats.withIssues}</div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700">
              Parsed Rows
            </div>
            {rows.length === 0 && (
              <div className="p-6 text-sm text-gray-500">No rows loaded.</div>
            )}
            {rows.length > 0 && (
              <div className="max-h-[60vh] overflow-auto">
                <table className="min-w-full text-xs leading-tight">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-2 py-0.5 text-left w-[42px]">#</th>
                      <th className="px-2 py-0.5 text-left w-[96px]">Release Date</th>
                      <th className="px-2 py-0.5 text-left w-[140px]">Program</th>
                      <th className="px-2 py-0.5 text-left w-[110px]">Booking</th>
                      <th className="px-2 py-0.5 text-left min-w-[160px]">Name</th>
                      <th className="px-2 py-0.5 text-left w-[140px]">CID / ChampID</th>
                      <th className="px-2 py-0.5 text-left w-[120px]">ODR Assessment</th>
                      <th className="px-2 py-0.5 text-left min-w-[160px]">Release Dates</th>
                      <th className="px-2 py-0.5 text-left min-w-[190px]">Booking Numbers</th>
                      <th className="px-2 py-0.5 text-left min-w-[160px]">Issues</th>
                      <th className="px-2 py-0.5 text-left w-[70px]">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const releaseDatesValue = row.releaseDates?.length ? row.releaseDates.join(', ') : '-';
                      const bookingArray = row.bookingArray || [];
                      const releaseDateDisplay = formatShortDate(row.releaseDate || row.releaseDateRaw);
                      const rowStripe = row.index % 2 === 0 ? 'bg-gray-50' : 'bg-white';
                      const idValue = row.cid || row.champid || '-';
                      const primaryBooking = row.booking || row.bookingRaw;
                      const bookingKey = `${primaryBooking || ''}`.replace(/\D/g, '');
                      const resolvedProgram = resolvedByRowId[row.index]?.program
                        || (bookingKey ? resolvedByBooking[bookingKey]?.program : '');
                      const resolvedParticipant = resolvedByRowId[row.index]?.participant
                        || (bookingKey ? resolvedByBooking[bookingKey]?.participant : undefined);
                      const displayProgram = resolvedProgram || row.program;
                      const bookingNumbers: string[] = [];
                      if (primaryBooking) {
                        bookingNumbers.push(primaryBooking);
                      }
                      for (const booking of bookingArray) {
                        if (!booking) continue;
                        if (bookingNumbers.includes(booking)) continue;
                        bookingNumbers.push(booking);
                      }
                      const bookingPreview: string[] = [];
                      let previewLength = 0;
                      for (const booking of bookingNumbers) {
                        const token = bookingPreview.length === 0 ? booking : `, ${booking}`;
                        if (previewLength + token.length > 30) break;
                        bookingPreview.push(booking);
                        previewLength += token.length;
                      }
                      const isTruncated = bookingPreview.length < bookingNumbers.length;
                      const displayIssues = row.issues.filter((issue) => {
                        if (issue === 'missing program' && displayProgram) return false;
                        return true;
                      });
                      const combinedIssues = [...displayIssues, ...(row.serverWarnings || [])].filter(Boolean);
                      return (
                        <React.Fragment key={`${row.index}-${row.bookingRaw}`}>
                          <tr className={rowStripe}>
                            <td className="px-2 py-0.5 text-gray-500">{row.index}</td>
                            <td className="px-2 py-0.5 text-gray-900 whitespace-nowrap">{releaseDateDisplay || '-'}</td>
                            <td className="px-2 py-0.5">
                              <div className="text-gray-900">
                                {displayProgram || '-'}
                                {row.bookingTable && ` · ${row.bookingTable}`}
                              </div>
                            </td>
                            <td className="px-2 py-0.5 text-gray-900">{row.booking || row.bookingRaw || '-'}</td>
                            <td className="px-2 py-0.5 text-gray-900">{row.name || '-'}</td>
                            <td className="px-2 py-0.5 text-gray-900">{idValue}</td>
                            <td className="px-2 py-0.5 text-gray-900 whitespace-nowrap">{row.odrAssessmentDate || '-'}</td>
                            <td className="px-2 py-0.5 text-gray-600">{releaseDatesValue}</td>
                            <td className="px-2 py-0.5 text-gray-600">
                              {bookingNumbers.length === 0 && '-'}
                              {bookingNumbers.length > 0 && (
                                <span>
                                  {bookingPreview.map((booking, index) => {
                                    const isPrimary = primaryBooking ? `${booking}` === `${primaryBooking}` : true;
                                    const valueClass = isPrimary ? 'text-gray-900' : 'text-gray-400';
                                    return (
                                      <span key={`${row.index}-${booking}`} className={valueClass}>
                                        {booking}{index < bookingPreview.length - 1 ? ', ' : ''}
                                      </span>
                                    );
                                  })}
                                  {isTruncated && <span className="text-gray-400">…</span>}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-0.5 text-gray-600">
                              {combinedIssues.length === 0 && <span className="text-green-600">OK</span>}
                              {combinedIssues.length > 0 && (
                                <span className="text-red-600">{combinedIssues.join(', ')}</span>
                              )}
                            </td>
                            <td className="px-2 py-0.5 text-gray-600">
                              <button
                                type="button"
                                className="text-xs text-gray-600 hover:text-gray-900"
                                onClick={() => setDetailRowId(row.index)}
                              >
                                Show
                              </button>
                            </td>
                          </tr>
                          <Dialog open={detailRowId === row.index} onOpenChange={(open: boolean) => {
                            if (open) return;
                            setDetailRowId(null);
                          }}>
                          <DialogContent className="max-w-6xl">
                            <DialogHeader>
                              <DialogTitle>Row {row.index}</DialogTitle>
                              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                <span className="font-semibold text-gray-700">A.txt</span>
                                <span>Release: {row.releaseDateRaw || row.releaseDate || '-'}</span>
                                <span>Booking: {row.bookingRaw || row.booking || '-'}</span>
                                <span>Program: {row.program || '-'}</span>
                                <span>Name: {row.name || '-'}</span>
                                <span>DOB: {row.dob || '-'}</span>
                                <span>CID: {row.cid || '-'}</span>
                              </div>
                            </DialogHeader>
                            {detailRow && (
                              <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
                                <div className="space-y-4">
                                  <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase">Issues</div>
                                    <div className="mt-1">
                                      {combinedIssues.length === 0 && <span className="text-green-600">OK</span>}
                                      {combinedIssues.length > 0 && (
                                        <span className="text-red-600">{combinedIssues.join(', ')}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase">Resolution Path</div>
                                    <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                      <div>
                                        <span className="text-gray-400">Program Source:</span>{' '}
                                        {resolvedByRowId[detailRow.index]?.program ? 'server resolver' : detailRow.program ? 'input' : 'missing'}
                                      </div>
                                      <div>
                                        <span className="text-gray-400">Participant Source:</span>{' '}
                                        {resolvedParticipant || detailRow.participant ? 'server resolver' : 'missing'}
                                      </div>
                                      <div>
                                        <span className="text-gray-400">Assessment Date Source:</span>{' '}
                                        {detailRow.odrAssessmentDate && detailChsSummary?.matches?.some((record) => record.admitDate && record.admitDate === detailRow.odrAssessmentDate)
                                          ? 'CHS admit_date'
                                          : detailRow.odrAssessmentDate
                                            ? 'server'
                                            : 'missing'}
                                      </div>
                                      <div>
                                        <span className="text-gray-400">CHS Matches:</span>{' '}
                                        {detailChsSummary?.matches?.length || 0}
                                      </div>
                                      <div className="sm:col-span-2">
                                        <span className="text-gray-400">CHS Booking Keys:</span>{' '}
                                        {detailChsSummary?.bookingKeys?.length ? detailChsSummary.bookingKeys.join(', ') : '-'}
                                      </div>
                                      <div className="sm:col-span-2">
                                        <span className="text-gray-400">CHS Booking Used:</span>{' '}
                                        {detailChsSummary?.chsRecord?.booking || '-'}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs font-semibold text-gray-500 uppercase">Scrubber Data</div>
                                    <div className="mt-2">
                                      <div className="text-xs font-semibold text-gray-500 uppercase">Input</div>
                                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                        <div><span className="text-gray-400">Release Date:</span> {detailRow.releaseDateRaw || '-'}</div>
                                        <div><span className="text-gray-400">Program:</span> {detailRow.program || '-'}</div>
                                        <div><span className="text-gray-400">Booking:</span> {detailRow.bookingRaw || '-'}</div>
                                        <div><span className="text-gray-400">Name:</span> {detailRow.name || '-'}</div>
                                        <div><span className="text-gray-400">DOB:</span> {detailRow.dob || '-'}</div>
                                        <div><span className="text-gray-400">CID:</span> {detailRow.cid || '-'}</div>
                                      </div>
                                      <div className="mt-3 text-xs font-semibold text-gray-500 uppercase">Resolved</div>
                                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                        <div><span className="text-gray-400">Program:</span> {displayProgram || '-'}</div>
                                        <div><span className="text-gray-400">Participant:</span> {resolvedParticipant || detailRow.participant || '-'}</div>
                                        <div><span className="text-gray-400">Primary Booking:</span> {detailRow.booking || detailRow.bookingRaw || '-'}</div>
                                        <div><span className="text-gray-400">Booking Table:</span> {detailRow.bookingTable || '-'}</div>
                                        <div><span className="text-gray-400">Booking From Release:</span> {detailRow.bookingFromRelease || '-'}</div>
                                        <div><span className="text-gray-400">Booking Numbers:</span> {bookingNumbers.length === 0 ? '-' : bookingNumbers.join(', ')}</div>
                                        <div><span className="text-gray-400">Booking Events:</span> {detailRow.bookingEvents?.length ? detailRow.bookingEvents.join(', ') : '-'}</div>
                                        <div><span className="text-gray-400">Release Dates:</span> {releaseDatesValue}</div>
                                        <div><span className="text-gray-400">ODR Assessment Date:</span> {detailRow.odrAssessmentDate || '-'}</div>
                                        <div><span className="text-gray-400">FIN:</span> {detailRow.fin || '-'}</div>
                                        <div><span className="text-gray-400">MRUN:</span> {detailRow.mrun || '-'}</div>
                                        <div><span className="text-gray-400">ChampID:</span> {detailRow.champid || '-'}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="space-y-4">
                                  <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs font-semibold text-gray-500 uppercase">CHS Data</div>
                                    <div className="mt-2">
                                      <div className="text-xs font-semibold text-gray-500 uppercase">CHS Record</div>
                                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                        <div><span className="text-gray-400">CHS Booking:</span> {detailRow.chsBooking || '-'}</div>
                                        <div><span className="text-gray-400">CHS Name:</span> {detailRow.chsName || '-'}</div>
                                        <div><span className="text-gray-400">CHS DOB:</span> {detailRow.chsDob || '-'}</div>
                                        <div><span className="text-gray-400">CHS Race:</span> {detailRow.chsRace || '-'}</div>
                                        <div><span className="text-gray-400">CHS Sex:</span> {detailRow.chsSex || '-'}</div>
                                        <div><span className="text-gray-400">CHS FIN:</span> {detailRow.chsFin || '-'}</div>
                                        <div><span className="text-gray-400">CHS MRN:</span> {detailRow.chsMrun || '-'}</div>
                                        <div><span className="text-gray-400">CHS Admit:</span> {detailChsSummary?.chsRecord?.admitDate || '-'}</div>
                                        <div><span className="text-gray-400">CHS Release:</span> {detailChsSummary?.chsRecord?.releaseDate || '-'}</div>
                                      </div>
                                      <div className="mt-3 text-xs font-semibold text-gray-500 uppercase">PCode Results</div>
                                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                        <div><span className="text-gray-400">PCode @ Assessment:</span> {detailRow.pcodeAtAssessment || '-'}</div>
                                        <div><span className="text-gray-400">PCode Date @ Assessment:</span> {detailRow.pcodeDateAtAssessment || '-'}</div>
                                        <div><span className="text-gray-400">PCode Source @ Assessment:</span> {detailRow.pcodeSourceAtAssessment || '-'}</div>
                                        <div><span className="text-gray-400">Building @ Assessment:</span> {detailRow.buildingAtAssessment || '-'}</div>
                                        <div><span className="text-gray-400">PCode @ Release:</span> {detailRow.pcodeAtRelease || '-'}</div>
                                        <div><span className="text-gray-400">PCode Date @ Release:</span> {detailRow.pcodeDateAtRelease || '-'}</div>
                                        <div><span className="text-gray-400">PCode Source @ Release:</span> {detailRow.pcodeSourceAtRelease || '-'}</div>
                                        <div><span className="text-gray-400">Building @ Release:</span> {detailRow.buildingAtRelease || '-'}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase">CHS Matches</div>
                                    <div className="mt-1 text-xs text-gray-600">
                                      {detailChsSummary?.matches?.length === 0 && <div>No CHS matches found.</div>}
                                      {detailChsSummary?.matches?.length ? (
                                        <div className="grid gap-1">
                                          {detailChsSummary.matches.slice(0, 10).map((record) => (
                                            <div key={`${record.bookingKey}-${record.index}`} className="flex flex-wrap gap-x-3">
                                              <span><span className="text-gray-400">Booking:</span> {record.booking}</span>
                                              <span><span className="text-gray-400">PCode:</span> {record.pcode || '-'}</span>
                                              <span><span className="text-gray-400">PCode Date:</span> {record.pcodeDate || '-'}</span>
                                              <span><span className="text-gray-400">Admit:</span> {record.admitDate || '-'}</span>
                                              <span><span className="text-gray-400">Release:</span> {record.releaseDate || '-'}</span>
                                            </div>
                                          ))}
                                          {detailChsSummary.matches.length > 10 && (
                                            <div className="text-gray-500">Showing first 10 matches.</div>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                          </Dialog>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="report">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700">
              <span>PLevel Report</span>
              <Button variant="outline" size="sm" onClick={handleDownloadReport} disabled={reportRows.length === 0}>
                Download CSV
              </Button>
            </div>
            {reportRows.length === 0 && (
              <div className="p-6 text-sm text-gray-500">No report rows available.</div>
            )}
            {reportRows.length > 0 && (
              <div className="max-h-[70vh] overflow-auto">
                <table className="min-w-full text-xs leading-tight">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      {reportFields.map((field) => (
                        <th key={field.key} className="px-2 py-0.5 text-left">
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {reportFields.map((field) => (
                          <td key={field.key} className="px-2 py-0.5 text-gray-700">
                            {(row as Record<string, string | number>)[field.key] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="stats">
          {!hasChsData && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
              Load the CHS report to enable stats.
            </div>
          )}
          {hasChsData && (
            <div className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Rows</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.total}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Assessment Missing PLevel</div>
                  <div className="text-lg font-semibold text-gray-900">{pcodeMissingCounts.assessmentMissing}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Release Missing PLevel</div>
                  <div className="text-lg font-semibold text-gray-900">{pcodeMissingCounts.releaseMissing}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Unresolved Rows</div>
                  <div className="text-lg font-semibold text-gray-900">{unresolvedRows.length}</div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm font-medium text-gray-700">Assessment vs Release Data</div>
                <div className="mt-3 max-h-[40vh] overflow-auto">
                  <table className="min-w-full text-xs leading-tight">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="px-2 py-0.5 text-left">Program</th>
                        <th className="px-2 py-0.5 text-left">Assessment Total</th>
                        <th className="px-2 py-0.5 text-left">Release Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessmentVsReleaseData.map((row) => (
                        <tr key={row.name} className="odd:bg-white even:bg-gray-50">
                          <td className="px-2 py-0.5 text-gray-700">{row.name}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.Assessment_Total}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.Release_Total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold text-gray-800">
                        <td className="px-2 py-0.5">Total</td>
                        <td className="px-2 py-0.5">{assessmentVsReleaseTotals.Assessment_Total}</td>
                        <td className="px-2 py-0.5">{assessmentVsReleaseTotals.Release_Total}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm font-medium text-gray-700">Priority Levels by System Data</div>
                <div className="mt-3 max-h-[45vh] overflow-auto">
                  <table className="min-w-full text-xs leading-tight">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="px-2 py-0.5 text-left">System</th>
                        <th className="px-2 py-0.5 text-left">P0</th>
                        <th className="px-2 py-0.5 text-left">P1</th>
                        <th className="px-2 py-0.5 text-left">P2</th>
                        <th className="px-2 py-0.5 text-left">P3</th>
                        <th className="px-2 py-0.5 text-left">P4</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priorityBySystemData.map((row) => (
                        <tr key={row.name} className="odd:bg-white even:bg-gray-50">
                          <td className="px-2 py-0.5 text-gray-700">{row.name}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.P0}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.P1}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.P2}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.P3}</td>
                          <td className="px-2 py-0.5 text-gray-700">{row.P4}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold text-gray-800">
                        <td className="px-2 py-0.5">Total</td>
                        <td className="px-2 py-0.5">{priorityTotals.P0}</td>
                        <td className="px-2 py-0.5">{priorityTotals.P1}</td>
                        <td className="px-2 py-0.5">{priorityTotals.P2}</td>
                        <td className="px-2 py-0.5">{priorityTotals.P3}</td>
                        <td className="px-2 py-0.5">{priorityTotals.P4}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700">
                  Legacy Report Data
                </div>
                {reportRows.length === 0 && (
                  <div className="p-6 text-sm text-gray-500">No report rows available.</div>
                )}
                {reportRows.length > 0 && (
                  <div className="max-h-[60vh] overflow-auto">
                    <table className="min-w-full text-xs leading-tight">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          {reportFields.map((field) => (
                            <th key={field.key} className="px-2 py-0.5 text-left">
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportRows.map((row, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {reportFields.map((field) => (
                              <td key={field.key} className="px-2 py-0.5 text-gray-700">
                                {(row as Record<string, string | number>)[field.key] ?? ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="graphics">
          {!hasChsData && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
              Load the CHS report to enable graphics.
            </div>
          )}
          {hasChsData && (
            <div className="space-y-6">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm font-medium text-gray-700">Assessment vs Release</div>
                <div className="mt-3 h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={assessmentVsReleaseData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={110} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Assessment_Total" name="Assessment" fill="#6366f1" />
                      <Bar dataKey="Release_Total" name="Release" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm font-medium text-gray-700">Priority Levels by System</div>
                <div className="mt-3 h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={priorityBySystemData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="P0" stackId="a" fill="#ef4444" name="P0" />
                      <Bar dataKey="P1" stackId="a" fill="#f97316" name="P1" />
                      <Bar dataKey="P2" stackId="a" fill="#facc15" name="P2" />
                      <Bar dataKey="P3" stackId="a" fill="#22c55e" name="P3" />
                      <Bar dataKey="P4" stackId="a" fill="#3b82f6" name="P4" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="unresolved">
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Assessment Missing PLevel</div>
                <div className="text-lg font-semibold text-gray-900">{pcodeMissingCounts.assessmentMissing}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Release Missing PLevel</div>
                <div className="text-lg font-semibold text-gray-900">{pcodeMissingCounts.releaseMissing}</div>
              </div>
              <div className="rounded border border-gray-200 p-3">
                <div className="text-gray-500">Unresolved Rows</div>
                <div className="text-lg font-semibold text-gray-900">{unresolvedRows.length}</div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="text-sm font-medium text-gray-700">Unresolved Issue Totals</div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 text-sm">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">CHS Record Not Found</div>
                  <div className="text-lg font-semibold text-gray-900">{issueTotals['chs record not found']}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">NO PCODE Near Assessment</div>
                  <div className="text-lg font-semibold text-gray-900">{issueTotals['NO PCODE near ODR Assessment Date']}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">NO PCODE Near Release</div>
                  <div className="text-lg font-semibold text-gray-900">{issueTotals['NO PCODE near Release Date']}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Missing PLevel Assessment</div>
                  <div className="text-lg font-semibold text-gray-900">{issueTotals['missing p level at assessment']}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-gray-500">Missing PLevel Release</div>
                  <div className="text-lg font-semibold text-gray-900">{issueTotals['missing p level at release']}</div>
                </div>
              </div>
            </div>
            {renderRowsTable(unresolvedRows, 'No unresolved rows.', 'Unresolved Rows')}
          </div>
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default PLevelReleaseScrub;
